import  BusinessObjectIndex from "./businessObjectIndex"

export const getBusinessobjectPools = <T, C>(context?: C) => {
  return new Promise<T>((resolve, reject) => {
    window.parent.primingcloud.businessobject.getBusinessobjectPools(
      (data: T) => resolve(data),
      (err: Error) => reject(err),
      context ? context : null
    )
  })
}

/**
 * @returns {Promise<number>} the id of the specified pool. If the pool is not found an exception "pool not found" is thrown
 * @param {string} poolName the given poolName
 **/
export const getPoolIdByName = (poolName: string): Promise<number> => {
  return new Promise((resolve, reject) => {
    window.parent.primingcloud.businessobject.getPoolIdByName(
      poolName,
      (data: number) => resolve(data),
      (err: Error) => reject(err)
    )
  })
}

/**
 * loads one BO entry from the specified pool
 * @returns {T} returns the BO entry object
 * @param {number} poolId the id of the pool we try to access
 * @param {number} thingId the thing we want to access, if we dont have a key then the value can be 0
 * @param {string} key the key of the entry we try to access
 **/
export const loadEntry = <T, C>(
  poolId: number,
  thingId: number,
  key: string,
  context?: C
) => {
  return new Promise<T>((resolve, reject) => {
    window.parent.primingcloud.businessobject.loadEntry(
      poolId,
      thingId,
      key,
      (data: T) => resolve(data),
      (err: Error) => reject(err), // TODO in case of error, the error cb is not called!
      context ? context : null
    )
  })
}

/**
 * loads multiple entries from the specified pool
 * @returns {Promise<T[]>} the content of the entries starting with the key after the specified keyFrom
 * @param {number} poolId the id of the pool we try to access. If the pool in invalid then a null pointer exception is thrown
 * @param {number} thingId the thing we want to access, if we dont have a key then the value can be 0
 * @param {string | null} keyFrom the key before the key we want to receive
 * @param {number} limit the amount of keys we want to get returned
 * @param {C} context context
 *
 * EXAMPLE: if the Pool contains values with the keys 1,2,3,4,5,6 and we call this function with key = 2 & limit = 3 then we will receive then content of the entries 3,4,5 in an array of T
 */
export const loadEntries = <T, C>(
  poolId: number,
  thingId: number,
  keyFrom: string | null,
  limit: number,
  context?: C
) => {
  return new Promise<T[]>((resolve, reject) => {
    window.parent.primingcloud.businessobject.loadEntries(
      poolId,
      thingId,
      keyFrom,
      limit + 1, // the array we get returned will always miss the one entry we tried to access with keyFrom. Therefore we always add one more to ensure that the amount of entries defined in limit will be equal to the length of the returned array (if possible)
      (data: T[]) => resolve(data),
      (err: Error) => reject(err),
      context ? context : null
    )
  })
}

/**
 * @returns {Promise<string[]>} returns all keys that appear in the DB AFTER the key param up to the specified limit
 * @param {number} poolId the id of the pool we try to access. If the pool in invalid then a null pointer exception is thrown
 * @param {number} thingId the thing we want to access, if we dont have a key then the value can be 0
 * @param {string | null} keyFrom the key of the entry we try to access. If the key is invalid (empty string) then a manager exception is thrown
 * @param {number} limit the amount of keys we want to get returned
 * @param {C} context context
 *
 * EXAMPLE: if the Pool contains values with the keys 1,2,3,4,5,6 and we call this function with key = 2 & limit = 3 then we will receive 3,4,5 in an array of strings
 */
export const list = <C>(
  poolId: number,
  thingId: number,
  keyFrom: string | null,
  limit: number,
  context?: C
) => {
  return new Promise<string[]>((resolve, reject) => {
    window.parent.primingcloud.businessobject.list(
      poolId,
      thingId,
      keyFrom,
      limit,
      (data: any) => {
        if (Array.isArray(data)) {
          const items: string[] = []

          for (const item of data) {
            items.push(item.getMdKey())
          }

          resolve(items)
        } else {
          resolve([])
        }
      },
      (err: Error) => reject(err),
      context ? context : null
    )
  })
}

export const listRedux = (
  poolId: number,
  thingId: number,
  mdKeyStart: string,
  limit: number,
  actionType: string
) => {
  window.parent.primingcloudRedux.businessobject.listRedux(
    poolId,
    thingId,
    mdKeyStart,
    limit,
    actionType
  )
}

/**
 * returns multiple entries found in BO Pool that match the query
 * @returns {string[]} array of keys that match the specified query
 * @param {query} query an instance of a class. Has to be defined by the Cloud devs TBD!
 * @param {number} poolId the id of the pool we try to access. If the pool in invalid then a null pointer exception is thrown
 * @param {number} thingId the thing we want to access, if we dont have a key then the value can be 0
 * @param {string | null} keyFrom the key before the key we want to receive
 * @param {number} limit the amount of keys we want to get returned
 * @param {C} context context
 *
 * EXAMPLE: if the pool contains entries with the key 1,2,3,4,5,6,7,8,9,10 and the query filter filters it down to 2,3,4,5,6,7,8 + the keyFrom is specified as 3 with limit = 4 then we receive 4,5,6,7 in an array of keys
 **/
export const query = <T, C>(
  query: BusinessObjectIndex,
  poolId: number,
  thingId: number,
  keyFrom: string | null,
  limit: number,
  context?: C
): Promise<Record<string, T>> => {
  return new Promise((resolve, reject) => {
    window.parent.primingcloud.businessobject.query(
      query,
      poolId,
      thingId,
      keyFrom,
      limit + 1,
      (data: Record<string, T>) => resolve(data),
      (err: Error) => reject(err),
      context ? context : null
    )
  })
}

/**
 * returns multiple entries found in BO Pool that match the query
 * @returns {any[]} array of objects that match the specified query
 * @param {query} query an instance of a class. Has to be defined by the Cloud devs TBD!
 * @param {number} poolId the id of the pool we try to access. If the pool in invalid then a null pointer exception is thrown
 * @param {number} thingId the thing we want to access, if we dont have a key then the value can be 0
 * @param {string | null} keyFrom the key before the key we want to receive
 * @param {number} limit the amount of keys we want to get returned
 * @param {C} context context
 *
 * EXAMPLE: if the pool contains entries with the key 1,2,3,4,5,6,7,8,9,10 and the query filter filters it down to 2,3,4,5,6,7,8 + the keyFrom is specified as 3 with limit = 4 then we receive 4,5,6,7 in an array of keys
 **/
export const queryData = <T, C>(
    query: BusinessObjectIndex,
    poolId: number,
    thingId: number,
    keyFrom: string | null,
    limit: number,
    context?: C
): Promise<any> => {
    return new Promise((resolve, reject) => {
        window.parent.primingcloud.businessobject.queryData(
            query,
            poolId,
            thingId,
            keyFrom,
            limit + 1,
            (data: Record<string, T>) => resolve(data),
            (err: Error) => reject(err),
            context ? context : null
        )
    })
}

/**
 * creates an entry within the specified pool. If the entry with this key already exists, then the BO entry will be overwritten
 * @returns {Promise<string>} if successful returns the key of the added entry, else error
 * @param {number} poolId the id of the pool we try to access. If the pool in invalid then a null pointer exception is thrown
 * @param {number} thingId the thing we want to access, if we dont have a key then the value can be 0
 * @param {string} key the key of the entry we try to access. If the key is invalid (empty string) then a manager exception is thrown
 * @param {T} value the value to be added into the pool
 * @param {any} context context
 **/
export const createEntry = <T, C>(
  poolId: number,
  thingId: number,
  key: string,
  value: T,
  context?: C
) => {
  return new Promise<string>((resolve, reject) => {
    window.parent.primingcloud.businessobject.createEntry(
      poolId,
      thingId,
      key,
      value,
      (data: null) => resolve(key),
      (err: Error) => reject(err),
      context ? context : null
    )
  })
}

/**
 * removes the entry from the specified pool
 * @returns {Promise<string>} if successful returns the key of the removed entry
 * @param {number} poolId the id of the pool we try to access. If the pool in invalid then a null pointer exception is thrown
 * @param {number} thingId the thing we want to access, if we dont have a key then the value can be 0
 * @param {string} key the key of the entry we try to access. If the key is invalid (empty string) then a manager exception is thrown
 */
export const deleteEntry = <C>(
  poolId: number,
  thingId: number,
  key: string,
  context?: C
): Promise<string> => {
  return new Promise((resolve, reject) => {
    window.parent.primingcloud.businessobject.deleteEntry(
      poolId,
      thingId,
      key,
      (data: null) => resolve(key),
      (err: Error) => reject(err),
      context ? context : null
    )
  })
}


/**
 * removes the entry from the specified pool
 * @returns {Promise<string>} if successful returns the key of the removed entry
 * @param {number} poolId the id of the pool we try to access. If the pool in invalid then a null pointer exception is thrown
 * @param {number} thingId the thing we want to access, if we dont have a key then the value can be 0
 * @param {string} key the key of the entry we try to access. If the key is invalid (empty string) then a manager exception is thrown
 */
 export const deleteEntries = <C>(
  poolId: number,
  thingId: number,
  keys: string[],
  context?: C
): Promise<string[]> => {
  return new Promise((resolve, reject) => {
    window.parent.primingcloud.businessobject.deleteEntries(
      poolId,
      thingId,
      keys,
      (data: null) => resolve(keys),
      (err: Error) => reject(err),
      context ? context : null
    )
  })
}

export const generateMap = () => {
  window.parent.primingcloud.businessobject.generateMap()
}
